import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { components } from '@/api/schema';
import { formatUZS } from '@/lib/format';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, Plus, Edit2, Trash2, Loader2, Tag, Download, Upload, Globe } from 'lucide-react';
import { ExportButtons } from '@/components/ExportButtons';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useBranches } from '@/hooks/api/branches';
import { useCategories } from '@/hooks/use-categories';
import ProductFormFields from '@/components/products/ProductFormFields';
import ProductImportDialog from '@/components/products/ProductImportDialog';
import AssignGlobalProducts from '@/components/AssignGlobalProducts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { MediaUploadField } from '@/components/media/MediaUploadField';
import {
  emptyProductForm, ProductFormState, productToForm, formToPayload, isProductFormValid,
  downloadProductTemplate, readProductsExcelWithCsv, ImportRow, formatTenors, parseTenors,
} from '@/lib/product-schema';

import { BranchFilter } from '@/components/BranchFilter';
import { PaginationControls } from '@/components/PaginationControls';
import { useTranslation } from 'react-i18next';

type Product = components['schemas']['ProductResource'];

const emptyForm = emptyProductForm;
const PAGE_SIZE = 25;

function errorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message) || fallback;
  }
  return fallback;
}

export default function Products() {
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<ProductFormState>(emptyForm);
  const [formTouched, setFormTouched] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: categories = [] } = useCategories();

  const { data: branches = [] } = useBranches();

  // FR-98: server-side pages of 25, as Lovable; search/category/branch filter on the server.
  const [page, setPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);
  useEffect(() => { setPage(1); }, [selectedCategory, branchFilter]);

  const productQuery = {
    search: debouncedSearch || undefined,
    category: selectedCategory !== 'all' ? selectedCategory : undefined,
    branch_id: branchFilter !== 'all' ? Number(branchFilter) : undefined,
    page,
    per_page: PAGE_SIZE,
  };
  const { data: productsData, isLoading } = useQuery({
    queryKey: ['/products', { params: { query: productQuery } }],
    placeholderData: (prev) => prev,
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/products', {
        params: { query: productQuery as never },
      });
      if (error) throw new Error(errorMessage(error, t('cat_common.loadProductsError')));
      return data as { data?: Product[]; meta?: { total: number; last_page: number } };
    },
  });
  const products: Product[] = productsData?.data ?? [];
  const totalProducts = productsData?.meta?.total ?? products.length;
  const totalPages = productsData?.meta?.last_page ?? 1;

  // Filial qoldiqlari — mahsulot qaysi filialda borligini ko'rsatish uchun
  const { data: stockRows = [] } = useQuery({
    queryKey: ['/stocks'],
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/stocks');
      if (error) throw new Error(errorMessage(error, t('cat_common.loadStocksError')));
      return data?.data ?? [];
    },
  });

  const branchNameById = new Map<number, string>(branches.map((b: { id: number; name: string }) => [b.id, b.name]));

  // product_id -> [{ branch_id, name, quantity }]
  const branchStockMap = new Map<number, { branch_id: number; name: string; quantity: number }[]>();
  for (const row of stockRows) {
    if (Number(row.quantity) <= 0) continue;
    const list = branchStockMap.get(row.product_id!) ?? [];
    list.push({ branch_id: row.branch_id!, name: branchNameById.get(row.branch_id!) ?? '—', quantity: Number(row.quantity) });
    branchStockMap.set(row.product_id!, list);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      // FR-98: «Asl narxi» goes to the default price list, on create and edit (as Lovable).
      const payload = { ...formToPayload(form), price: parseFloat(form.price) || 0 };
      if (editingId) {
        const { error } = await apiClient.PATCH('/products/{product}', {
          params: { path: { product: editingId } },
          body: payload,
        });
        if (error) throw new Error(errorMessage(error, t('cat_products.updateError')));
      } else {
        const { error } = await apiClient.POST('/products', { body: payload });
        if (error) throw new Error(errorMessage(error, t('cat_products.addError')));
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/products'] });
      closeForm();
      toast({ title: editingId ? t('cat_products.updated') : t('cat_products.added') });
    },
    onError: (err: Error) => {
      toast({ title: t('cat_common.error'), description: err.message, variant: 'destructive' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await apiClient.DELETE('/products/{product}', { params: { path: { product: id } } });
      if (error) throw new Error(errorMessage(error, t('cat_products.deleteError')));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/products'] });
      setDeleteId(null);
      toast({ title: t('cat_products.deleted') });
    },
    onError: (err: Error) => {
      toast({ title: t('cat_common.error'), description: err.message, variant: 'destructive' });
    },
  });

  // ===== Excel import (FR-18: server validates/inserts each row independently) =====
  const fileRef = useRef<HTMLInputElement>(null);
  const [importRows, setImportRows] = useState<ImportRow[] | null>(null);
  const [importCsv, setImportCsv] = useState<string | null>(null);
  const [importDialog, setImportDialog] = useState(false);
  const [importing, setImporting] = useState(false);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const { rows, csv } = await readProductsExcelWithCsv(file);
      setImportRows(rows);
      setImportCsv(csv);
      setImportDialog(true);
    } catch (err: any) {
      toast({ title: t('cat_common.error'), description: err.message, variant: 'destructive' });
    }
  };

  const executeImport = async () => {
    if (!importCsv) return;
    setImporting(true);
    try {
      const body = new FormData();
      body.append('file', new Blob([importCsv], { type: 'text/csv' }), 'import.csv');
      const { data, error } = await apiClient.POST('/products/import', {
        body: body as unknown as never,
      });
      if (error) throw new Error(errorMessage(error, t('cat_products.importError')));
      const imported = data?.imported ?? 0;
      const errors = data?.errors ?? [];
      toast({
        title: t('cat_products.importedCount', { n: imported }),
        description: errors.length > 0 ? t('cat_products.importRowErrors', { n: errors.length, row: errors[0].row }) : undefined,
        variant: errors.length > 0 ? 'destructive' : undefined,
      });
      setImportDialog(false);
      setImportRows(null);
      setImportCsv(null);
      queryClient.invalidateQueries({ queryKey: ['/products'] });
    } catch (err: any) {
      toast({ title: t('cat_products.importError'), description: err.message, variant: 'destructive' });
    } finally { setImporting(false); }
  };

  const closeForm = () => { setShowForm(false); setEditingId(null); setForm(emptyForm); setFormTouched(false); };

  const openEdit = (p: Product) => {
    setEditingId(p.id!);
    setForm(productToForm(p));
    setFormTouched(false);
    setShowForm(true);
  };

  const filtered = products;

  const categoryNames: string[] = categories.map((c) => c.name!);

  const productExportData = filtered.map((p) => ({
    nomi: p.name, sku: p.sku, brend: p.brend_nomi || '', atribut: p.atribut_nomi || '',
    mhik: p.mhik || '', mxik_nomi: p.mxik_nomi || '', shtrix: p.barcode || '',
    kategoriya: p.category, birlik: p.unit,
    narxi: p.sale_price || 0,
    muddatlar: parseTenors(p.allowed_tenors).join(', '),
    murabaha: p.is_murabaha_eligible ? t('cat_common.yes') : t('cat_common.no'),
    filiallar: (branchStockMap.get(p.id!) ?? []).map(b => `${b.name}: ${b.quantity}`).join('; '),
  }));
  const productExportColumns = [
    { header: t('cat_common.name'), key: 'nomi' }, { header: 'SKU', key: 'sku' },
    { header: t('cat_products.brandName'), key: 'brend' }, { header: t('cat_common.attribute'), key: 'atribut' },
    { header: t('cat_products.mxikCode'), key: 'mhik' }, { header: t('cat_products.mxikName'), key: 'mxik_nomi' },
    { header: t('cat_products.barcode'), key: 'shtrix' }, { header: t('cat_common.category'), key: 'kategoriya' },
    { header: t('cat_common.unit'), key: 'birlik' }, { header: t('cat_products.price'), key: 'narxi' },
    { header: t('cat_products.branchesStock'), key: 'filiallar' },
    { header: t('cat_products.tenors'), key: 'muddatlar' },
    { header: 'Murabaha', key: 'murabaha' },
  ];

  return (
    <div className="p-4 md:p-6 w-full animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-foreground">{t('cat_products.title')}</h1>
      </div>

      <Tabs defaultValue="my" className="space-y-4">
        <TabsList>
          <TabsTrigger value="my">{t('cat_products.myProducts')}</TabsTrigger>
          <TabsTrigger value="global" className="gap-1.5"><Globe size={14} /> {t('cat_products.globalCatalog')}</TabsTrigger>
        </TabsList>

        <TabsContent value="my" className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2 flex-wrap">
            <ExportButtons data={productExportData} columns={productExportColumns} fileName="mahsulotlar" title={t('cat_products.listTitle')} />
            <Button size="sm" variant="outline" onClick={() => downloadProductTemplate()}>
              <Download size={14} className="mr-1" /> {t('cat_products.template')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}>
              <Upload size={14} className="mr-1" /> {t('cat_products.excelImport')}
            </Button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleFileSelect} className="hidden" />
          </div>
          <Button size="sm" onClick={() => { setEditingId(null); setForm(emptyForm); setFormTouched(false); setShowForm(true); }}>
            <Plus size={14} className="mr-1" /> {t('cat_products.add')}
          </Button>
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input placeholder={t('cat_common.search')} value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
          </div>
          <BranchFilter value={branchFilter} onChange={setBranchFilter} />
          <div className="flex gap-1 overflow-x-auto">
            <button onClick={() => setSelectedCategory('all')}
              className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${selectedCategory === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'}`}>
              {t('cat_common.all')}
            </button>
            {categoryNames.map(cat => (
              <button key={cat} onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${selectedCategory === cat ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'}`}>
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-card border border-border rounded-xl overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="text-left text-xs font-medium text-muted-foreground p-3 sticky left-0 bg-muted/50 z-10">{t('cat_common.name')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">SKU</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_common.brand')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_common.attribute')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_products.mxikCode')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_products.mxikName')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_products.barcode')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_products.branchesStock')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_common.category')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_common.unit')}</th>
                <th className="text-right text-xs font-medium text-muted-foreground p-3">{t('cat_products.price')}</th>
                <th className="text-right text-xs font-medium text-muted-foreground p-3">{t('cat_common.discount')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cat_products.tenors')}</th>
                <th className="text-left text-xs font-medium text-muted-foreground p-3">Murabaha</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan={15} className="p-8 text-center text-muted-foreground">{t('cat_common.loading')}</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={15} className="p-8 text-center text-muted-foreground">{t('cat_products.notFound')}</td></tr>
              ) : (
                filtered.map((p) => {
                  const hasSale = (p.sale_price ?? 0) > 0;
                  const discount = hasSale ? (Number(p.price) || 0) - Number(p.sale_price) : 0;
                  return (
                    <tr key={p.id} className="group border-b border-border last:border-0 hover:bg-muted/30 transition-colors">
                      <td className="p-3 text-sm text-foreground font-medium sticky left-0 bg-card group-hover:bg-muted/30 z-10">{p.name}</td>
                      <td className="p-3 text-sm text-muted-foreground font-mono whitespace-nowrap">{p.sku}</td>
                      <td className="p-3 text-sm text-muted-foreground">{p.brend_nomi || '—'}</td>
                      <td className="p-3 text-sm text-muted-foreground">{p.atribut_nomi || '—'}</td>
                      <td className="p-3 text-xs text-muted-foreground font-mono whitespace-nowrap">{p.mhik || '—'}</td>
                      <td className="p-3 text-sm text-muted-foreground">{p.mxik_nomi || '—'}</td>
                      <td className="p-3 text-xs text-muted-foreground font-mono whitespace-nowrap">{p.barcode || '—'}</td>

                      <td className="p-3 text-xs text-muted-foreground">
                        {(branchStockMap.get(p.id!) ?? []).length === 0
                          ? '—'
                          : (branchStockMap.get(p.id!) ?? []).map(b => (
                              <span key={b.branch_id} className="block whitespace-nowrap">{b.name}: {b.quantity}</span>
                            ))}
                      </td>
                      <td className="p-3 text-sm text-muted-foreground">{p.category}</td>
                      <td className="p-3 text-sm text-muted-foreground">{p.unit}</td>
                      <td className="p-3 text-sm text-right font-mono">
                        {hasSale ? (
                          <span className="text-success font-medium">{formatUZS(p.sale_price!)}</span>
                        ) : (
                          <span className="text-foreground">{formatUZS(Number(p.price) || 0)}</span>
                        )}
                      </td>
                      <td className="p-3 text-sm text-right font-mono">
                        {discount > 0 ? (
                          <Badge variant="outline" className="text-xs border-warning/30 text-warning bg-warning/5">
                            <Tag size={10} className="mr-1" />
                            {formatUZS(discount)}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">0</span>
                        )}
                      </td>
                      <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{formatTenors(parseTenors(p.allowed_tenors))}</td>
                      <td className="p-3">{p.is_murabaha_eligible && <Badge className="text-xs bg-success/10 text-success border-0">{t('cat_common.yes')}</Badge>}</td>
                      <td className="p-3">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground"><Edit2 size={14} /></button>
                          <button onClick={() => setDeleteId(p.id!)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive"><Trash2 size={14} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <PaginationControls page={page} totalPages={totalPages} total={totalProducts} pageSize={PAGE_SIZE} onPrev={() => setPage(p => Math.max(1, p - 1))} onNext={() => setPage(p => Math.min(totalPages, p + 1))} hasPrev={page > 1} hasNext={page < totalPages} />
        </TabsContent>

        <TabsContent value="global">
          <AssignGlobalProducts />
        </TabsContent>
      </Tabs>

      {/* Product Create/Edit Dialog */}
      <Dialog open={showForm} onOpenChange={v => { if (!v) closeForm(); }}>
        <DialogContent className="max-w-none w-screen h-[100dvh] max-h-[100dvh] top-0 left-0 translate-x-0 translate-y-0 rounded-none p-0 gap-0 flex flex-col overflow-hidden">
          <div className="flex flex-col h-full min-h-0 max-w-6xl mx-auto w-full">
            <DialogHeader className="shrink-0 px-6 py-5 border-b text-left">
              <DialogTitle>{editingId ? t('cat_products.editTitle') : t('cat_products.newTitle')}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6 space-y-4">
              {editingId && (
                <MediaUploadField
                  kind="product_photo"
                  targetId={String(editingId)}
                  value={form.photo_url ?? ''}
                  onChange={(url) => setForm(f => ({ ...f, photo_url: url }))}
                  label={t('cat_products.photo')}
                  shape="square"
                />
              )}
              <ProductFormFields
                form={form}
                setForm={setForm}
                categories={categoryNames}
                isEditing={!!editingId}
                showErrors={formTouched}
                showOriginalPrice
              />
            </div>
            <DialogFooter className="shrink-0 mt-0 px-6 py-4 border-t bg-card flex flex-row flex-wrap items-center justify-between gap-3">

              <div className="flex items-center gap-2">
                {editingId && (
                  <Button
                    variant="destructive"
                    onClick={() => setDeleteId(editingId)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 size={14} className="mr-1" />
                    {t('cat_common.delete')}
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <Button variant="outline" onClick={closeForm}>{t('cat_common.cancel')}</Button>
                <Button
                  onClick={() => {
                    setFormTouched(true);
                    if (!isProductFormValid(form)) {
                      toast({ title: t('cat_products.allRequired'), variant: 'destructive' });
                      return;
                    }
                    saveMutation.mutate();
                  }}
                  disabled={saveMutation.isPending}
                >
                  {saveMutation.isPending ? <><Loader2 size={14} className="animate-spin mr-1" /> {t('cat_common.saving')}</> : t('cat_common.save')}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <ProductImportDialog
        open={importDialog}
        rows={importRows}
        importing={importing}
        onClose={() => { setImportDialog(false); setImportRows(null); setImportCsv(null); }}
        onConfirm={executeImport}
      />

      {/* Product Delete */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('cat_products.deleteTitle')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('cat_products.deleteConfirm')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>{t('cat_common.cancel')}</Button>
            <Button variant="destructive" onClick={() => deleteId && deleteMutation.mutate(deleteId)} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending ? <Loader2 size={14} className="animate-spin mr-1" /> : <Trash2 size={14} className="mr-1" />}
              {t('cat_common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
